import {
  useState
} from "react";
import {
  useLocation,
  useNavigate,
  Link
} from "react-router-dom";
import {
  resetPassword
} from "../api/passwordApi";
import {
  LockKeyhole,
  Eye,
  EyeOff,
  ArrowLeft,
  CheckCircle2,
  Check,
  Circle
} from "lucide-react";

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Input
} from "../components/ui";
import {
  useToast
} from "../context/ToastContext";

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || "";

  // Functional React States
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const toast = useToast();

  // Toggle Visibility Layout States
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Real-Time Requirement Checklist Evaluators
  const hasLength = password.length >= 8;
  const hasComplexity = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{1,}$/.test(password);
  const passwordsMatch = password === confirmPassword && confirmPassword !== "";
  const isSubmitDisabled = !(hasLength && hasComplexity && passwordsMatch) || loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitDisabled) return;

    try {
      setLoading(true);

      await resetPassword(email, password);
      
      setIsSuccess(true);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to reset password. Please check your network and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950 flex flex-col justify-between p-4 sm:p-6 lg:p-8 antialiased">
      <div />

      <main className="w-full max-w-md mx-auto my-auto">
        <Card className="border border-neutral-200 dark:border-neutral-800 shadow-none bg-white dark:bg-neutral-900">
          <CardHeader className="text-center pt-8 pb-4 border-b border-neutral-100 dark:border-neutral-800">
            <div className="w-12 h-12 bg-neutral-900 rounded-xl flex items-center justify-center mx-auto mb-3 text-white shadow-xs">
              <LockKeyhole className="w-6 h-6" />
            </div>
            <CardTitle className="text-xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
      Reset Your Password
            </CardTitle>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
              Secure your account with a new password
            </p>
          </CardHeader>

          <CardContent className="p-6">
            {!isSuccess ? (
              <div className="space-y-5">
                

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* New Password Input */}
                  <div className="relative">
                    <Input
                      label="New Password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-[34px] text-neutral-400 dark:text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-200 transition-colors p-1"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>

                  {/* Confirm Password Input */}
                  <div className="relative">
                    <Input
                      label="Confirm New Password"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-[34px] text-neutral-400 dark:text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-200 transition-colors p-1"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>

                  {/* Password Requirement Checklist */}
                  <div className="bg-neutral-50/80 rounded-lg p-3.5 border border-neutral-200 dark:border-neutral-800 space-y-2">
                    <p className="text-[10px] font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider mb-1">
                      Security Requirements
                    </p>
                    
                    <div className="flex items-center gap-2 text-xs">
                      {hasLength ? (
                        <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                      ) : (
                        <Circle className="w-3.5 h-3.5 text-neutral-300 dark:text-neutral-600 shrink-0" />
                      )}
                      <span className={hasLength ? "text-neutral-900 dark:text-neutral-100 font-medium" : "text-neutral-500 dark:text-neutral-400"}>
                        At least 8 characters long
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-xs">
                      {hasComplexity ? (
                        <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                      ) : (
                        <Circle className="w-3.5 h-3.5 text-neutral-300 dark:text-neutral-600 shrink-0" />
                      )}
                      <span className={hasComplexity ? "text-neutral-900 dark:text-neutral-100 font-medium" : "text-neutral-500 dark:text-neutral-400"}>
                        Mix of letters, numbers & symbols
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-xs">
                      {passwordsMatch ? (
                        <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                      ) : (
                        <Circle className="w-3.5 h-3.5 text-neutral-300 dark:text-neutral-600 shrink-0" />
                      )}
                      <span className={passwordsMatch ? "text-neutral-900 dark:text-neutral-100 font-medium" : "text-neutral-500 dark:text-neutral-400"}>
                        Passwords must match
                      </span>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    variant="primary"
                    isLoading={loading}
                    disabled={isSubmitDisabled}
                    className="w-full py-2.5 text-xs font-semibold mt-2"
                  >
                    Reset Password
                  </Button>
                </form>

                <div className="text-center pt-2">
                  <Link
                    to="/"
                    className="text-xs font-medium text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 inline-flex items-center justify-center gap-1.5 transition-colors group"
                  >
                    <ArrowLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5" /> 
                    Back to Login
                  </Link>
                </div>
              </div>
            ) : (
              <div className="text-center py-4 space-y-4">
                <div className="w-14 h-14 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">Password Reset Successful</h2>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 leading-relaxed">
                    Your account credentials have been updated. You can now use your new password to sign into the system.
                  </p>
                </div>
                
                <Button
                  variant="primary"
                  onClick={() => navigate("/")}
                  className="w-full text-xs font-semibold py-2.5"
                >
                  Go to Login
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      <footer className="py-4 text-center">
        <p className="text-[11px] text-neutral-400 dark:text-neutral-500">© 2026 TFC elevate. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default ResetPassword;